import Container from "lib/components/Container"
import Text from "lib/components/Text"
import { NextSeo } from "next-seo"
import { useContext, useMemo, useState } from "react"
import { LanguageContext } from "context/LanguageContext"
import { useRouter } from "next/router"
import url from "url"
import Script from "next/script"
import { UploadFileProvider } from "context/UploadContext"
import JoinProviderFrom from "@components/Form/JoinProviderForm"

export const getServerSideProps = async (ctx) => {
  let activeLocale = ctx.locale
  const language = ctx.query.lang

  if (language) {
    activeLocale = language
  }

  const dataLanguage = await import(
    `../public/locales/${activeLocale.toUpperCase()}/translations.json`
  )

  const descriptionName = dataLanguage.description_seo_provider_form
  const metaTitle = dataLanguage.title_seo_provider_form

  return {
    props: {
      descriptionName,
      metaTitle,
    },
  }
}

function ProviderForm({ metaTitle, descriptionName }) {
  const { useTranslation: t } = useContext(LanguageContext)
  const [stepListItem, setStepListItem] = useState([])
  const router = useRouter()
  const { pathname } = url.parse(router.asPath)

  useMemo(() => {
    const dataStepListItem = [
      {
        title: t("provider_form_step_one_title"),
        description: t("provider_form_step_one_description"),
      },
      {
        title: t("provider_form_step_two_title"),
        description: t("provider_form_step_two_description"),
      },
      {
        title: t("provider_form_step_three_title"),
        description: t("provider_form_step_three_description"),
      },
      {
        title: t("provider_form_step_four_title"),
        description: t("provider_form_step_four_description"),
      },
    ]
    setStepListItem(dataStepListItem)
  }, [t])

  return (
    <div>
      <Script src="https://www.google.com/recaptcha/api.js" strategy="afterInteractive" />
      <NextSeo
        title={metaTitle}
        description={descriptionName}
        canonical={`${process.env.NEXT_PUBLIC_URL}${pathname}`}
        noindex
        openGraph={{
          type: "website",
          locale: "en_ID",
          url: `${process.env.NEXT_PUBLIC_URL}${pathname}`,
          title: `${metaTitle}`,
          site_name: `${metaTitle}`,
          description: descriptionName,
          images: [
            {
              url: "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1721139864/open-graph/page/provider.jpg",
              alt: `${metaTitle}`,
              width: 800,
              height: 600,
            },
          ],
        }}
      />
      <div className="relative">
        <div
          className="w-[200px] h-[200px] sm:w-[500px] sm:h-[500px] absolute mt-0 right-0 -z-10 rounded-full overflow-hidden"
          style={{
            background:
              "linear-gradient(220deg, rgba(38,132,255,1) 0%, rgba(155,198,255,1) 0%, rgba(255,255,255,1) 100%)",
            filter: "blur(100px)",
          }}
        />
      </div>
      <div className="relative">
        <div
          className="w-[250px] h-[250px] sm:w-[500px] sm:h-[500px] absolute mt-[200%] sm:mt-[52%] left-[-20%] -z-10 rounded-full overflow-hidden"
          style={{
            background:
              "linear-gradient(220deg, rgba(38,132,255,1) 0%, rgba(155,198,255,1) 0%, rgba(255,255,255,1) 100%)",
            filter: "blur(100px)",
          }}
        />
      </div>
      <div className="relative z-10">
        <Container>
          <div className="w-full sm:w-3/4 mx-auto mt-8 sm:mt-12">
            <h1 className="text-primary text-[24px] sm:text-[40px] leading-[28.8px] sm:leading-[48px] font-semibold text-center">
              {t("provider_form_title")}
            </h1>
            <Text alignText="center" customClass="mt-4">
              {t("provider_form_description")}
            </Text>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 my-20">
            <div className="mr-0 sm:mr-12">
              <div className="mt-2">
                <Text customClass="font-semibold text-[18px] sm:text-[20px] text-center sm:text-left">
                  {t("provider_form_step_title")}
                </Text>
              </div>
              <div className="mt-6 mb-8">
                {stepListItem.map((item, index) => (
                  <div
                    key={index}
                    className={`flex gap-4 sm:gap-12 ${
                      index === 0 ? "mt-8 sm:mt-12" : "mt-10 sm:mt-16"
                    } relative`}>
                    <div
                      className={`absolute border-l border-neutral-40 ${
                        index !== stepListItem.length - 1 ? "h-48 sm:h-56" : "h-0"
                      } ml-5 sm:ml-8 z-0`}
                    />
                    <div className="absolute border-t border-neutral-40 ml-1 sm:ml-0 w-12 mt-5 sm:w-24 sm:mt-8 z-0" />
                    <div className="relative z-10">
                      <div className="flex items-center justify-center p-4 bg-primary rounded-md w-10 h-10 sm:w-16 sm:h-16">
                        <Text color="light" customClass="font-semibold text-base sm:text-[24px]">
                          {index + 1}
                        </Text>
                      </div>
                    </div>
                    <div>
                      <Text customClass="text-base sm:text-[20px] font-semibold mb-0 sm:mb-2">
                        {item.title}
                      </Text>
                      <Text customClass="text-sm sm:text-base">{item.description}</Text>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-0 sm:border-l border-neutral-40 mb-16 sm:mb-[150px]">
              <UploadFileProvider>
                <JoinProviderFrom />
              </UploadFileProvider>
            </div>
          </div>
        </Container>
      </div>
    </div>
  )
}

export default ProviderForm
